import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useData } from "../../contexts/DataContext";
import { studentApi, sectionApi } from "../../api/services";
import toast from "react-hot-toast";
import { Loader, Users, Search, X, CheckCircle2 } from "lucide-react";
import LoadingOverlay from "../../ui/LoadingOverlay";
import ParentForm from "../Parents/ParentForm";

const StudentForm = () => {
  const { isAdmin, isTeacher, user, orgType, loading: authLoading } = useAuth();
  const { classes: apiClasses, parents: apiParents, addParent, loading: dataLoading, getClassesForTeacher } = useData();
  const navigate = useNavigate();
  const { id } = useParams();
  const [student, setStudent] = useState(null);
  const allowedUnits = useMemo(() => {
    return apiClasses?.map((cls) => ({ id: cls._id, name: cls.unitName })) || [];
  }, [apiClasses]);

  const teacherUnitIds = useMemo(() => allowedUnits.map(u => u.id), [allowedUnits]);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    middleName: "",
    email: "",
    studentRegistrationId: "",
    dateOfBirth: "",
    gender: "male",
    phoneNumber: "",
    alternatePhoneNumber: "",
    address: {
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    },
    status: "active",
    admissionDate: "",
    parentId: "",
    unitId: allowedUnits[0]?.id || "",
    sectionId: "",
  });

  const [errors, setErrors] = useState({});
  const [showParentForm, setShowParentForm] = useState(false);
  
  const [sections, setSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [loading, setLoading] = useState(false);
  const [parentSearchTerm, setParentSearchTerm] = useState("");
  const [parentResults, setParentResults] = useState([]);
  const [showParentResults, setShowParentResults] = useState(false);
  const [loadingStudent, setLoadingStudent] = useState(false);
  const [parentSubmitting, setParentSubmitting] = useState(false);

  // Load existing student data if editing
  useEffect(() => {
    const fetchStudent = async () => {
      if (id) {
        setLoadingStudent(true);
        try {
          const response = await studentApi.getStudentById(id);
          if (response.success && response.data) {
            const data = response.data;
            setStudent(data);
            // Extract IDs from nested objects if they exist
            const extractedUnitId = data.enrollment?.unitId?._id || data.enrollment?.unitId || (typeof data.unitId === 'object' ? data.unitId?._id : data.unitId);
            const extractedParentId = typeof data.parentId === 'object' ? data.parentId?._id : data.parentId;
            
            setForm({
              firstName: data.firstName || "",
              lastName: data.lastName || "",
              middleName: data.middleName || "",
              email: data.email || "",
              studentRegistrationId: data.studentRegistrationId || "",
              dateOfBirth: data.dateOfBirth ? data.dateOfBirth.split('T')[0] : "",
              gender: data.gender || "male",
              phoneNumber: data.phoneNumber || "",
              alternatePhoneNumber: data.alternatePhoneNumber || "",
              address: {
                street: data.address?.street || "",
                city: data.address?.city || "",
                state: data.address?.state || "",
                postalCode: data.address?.postalCode || "",
                country: data.address?.country || "",
              },
              status: data.status || "active",
              admissionDate: data.admissionDate ? data.admissionDate.split('T')[0] : "",
              parentId: extractedParentId || "",
              unitId: extractedUnitId || form.unitId,
              sectionId: data.enrollment?.sectionId?._id || data.enrollment?.sectionId || data.sectionId?._id || data.sectionId || "", 
            });
          }
        } catch (error) {
          const message = error.message || "Failed to load student data";
          toast.error(message);
        } finally {
          setLoadingStudent(false);
        }
      }
    };
    fetchStudent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, apiClasses]);

  // Fetch sections when unitId changes
  useEffect(() => {
    // Reset sections immediately when unit changes
    setSections([]);
    
    const fetchSections = async () => {
      if (!form.unitId) {
        return;
      }
      
      setLoadingSections(true);
      try {
        const response = await sectionApi.getSectionsByUnit(form.unitId);
        if (response.success) {
          setSections(response.data || []);
        }
      } catch (error) {
      } finally {
        setLoadingSections(false);
      }
    };

    fetchSections();
  }, [form.unitId]);

  // Helper to check if phone is required based on org type
  const isPhoneRequired = () => {
    if (!orgType) {
        const storedOrgType = localStorage.getItem("organizationType");
        if (!storedOrgType) return true; // Default to required if unknown
        return !['SCHOOL', 'INSTITUTE'].includes(storedOrgType.toUpperCase());
    }
    return !['SCHOOL', 'INSTITUTE'].includes(orgType.toUpperCase());
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "First name is required";
    if (!form.lastName.trim()) e.lastName = "Last name is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Valid email required";
    if (!form.studentRegistrationId.trim())
      e.studentRegistrationId = "Registration ID required";
    if (!form.unitId) e.unitId = "Class/Unit required";
    
    // New Validation Logic for Sections
    if (sections.length > 0 && !form.sectionId) {
        e.sectionId = "Section is required for this class";
    }

    if (!form.dateOfBirth) e.dateOfBirth = "Date of birth is required";
    if (!form.gender) e.gender = "Gender is required";
    if (isPhoneRequired() && !form.phoneNumber.trim())
      e.phoneNumber = "Phone number is required";
    if (!form.address.street.trim()) e.street = "Street address is required";
    if (!form.address.city.trim()) e.city = "City is required";
    if (!form.address.state.trim()) e.state = "State is required";
    if (!form.address.postalCode.trim())
      e.postalCode = "Postal code is required";
    if (!form.address.country.trim()) e.country = "Country is required";
    if (!form.admissionDate) e.admissionDate = "Admission date is required";
    // Parent is now optional - students can be added without parents
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      if (id) {
        // Update existing student
        const response = await studentApi.updateStudentById(id, form);
        if (response.success) {
          toast.success("Student updated successfully");
          navigate("/students");
        } else {
          throw new Error(response.message || "Failed to update student");
        }
      } else {
        // Create new student
        const response = await studentApi.registerStudent(form);
        if (response.success) {
          toast.success("Student created successfully");
          navigate("/students");
        } else {
          throw new Error(response.message || "Failed to create student");
        }
      }
    } catch (error) {
      const message = error.message || (id ? "Error updating student" : "Error creating student");
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (parentData) => {
    setParentSubmitting(true);
    try {
      // Create new parent using context method to trigger refresh
      const response = await addParent(parentData);

      if (response.success) {
        toast.success(response.message || "Parent created successfully!");
        setShowParentForm(false);
        
        // Extract ID safely handling different response structures
        const newParentId = response.data?.data?._id || response.data?._id || response.data?.id;
        
        if (newParentId) {
          setForm(prev => ({ ...prev, parentId: newParentId }));
        } else {
          toast.error("Parent created but could not be selected automatically.");
        }
      } else {
        // Handle non-success response with structured error
        const message = response?.message || response?.error || "Unknown error";
        toast.error("Error creating parent: " + message);
      }
    } catch (error) {
      // Use structured error message from backend
      const message = error.message || "Failed to save parent";
      toast.error(message);
      // Rethrow so the form component can display inline errors
      throw error;
    } finally {
      setParentSubmitting(false);
    }
  };

  useEffect(() => {
    if (allowedUnits.length > 0 && !form.unitId) {
      setForm((prev) => ({ ...prev, unitId: allowedUnits[0].id }));
    }
  }, [allowedUnits]);

  // Parent Search Logic
  useEffect(() => {
    if (parentSearchTerm.trim().length > 1) {
      const results = apiParents?.filter(parent => 
        parent.name.toLowerCase().includes(parentSearchTerm.toLowerCase()) ||
        parent.phoneNumber.includes(parentSearchTerm)
      ).slice(0, 5); // Limit to 5 results
      setParentResults(results || []);
      setShowParentResults(true);
    } else {
      setParentResults([]);
      setShowParentResults(false);
    }
  }, [parentSearchTerm, apiParents]);

  const selectedParent = useMemo(() => {
    return apiParents?.find(p => p._id === form.parentId);
  }, [form.parentId, apiParents]);

  if (dataLoading.classes || (id && loadingStudent) || authLoading) {
    return <LoadingOverlay />;
  }

  // Show "No Classes Assigned" full-screen block only for NEW student mode
  // For existing students, we show the form even if assignments are weird
  if (allowedUnits.length === 0 && !id) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-8 text-center bg-white rounded-lg shadow-sm">
        <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 15%, white)' }}>
          <Users className="w-8 h-8" style={{ color: 'var(--primary-color, #4f46e5)' }} />
        </div>
        <h2 className="mb-2 text-2xl font-bold text-gray-900">
          {isTeacher ? "No Classes Assigned" : "No Classes Found"}
        </h2>
        <p className="max-w-md mb-8 text-gray-500">
          {isTeacher 
            ? "You haven't been assigned to any classes yet. Please contact your administrator to get assigned to a class."
            : "You need to create a class before you can add students. Classes help organize your students and manage their data effectively."
          }
        </p>
        <div className="flex gap-4">
          <button
            onClick={() => navigate("/students")}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Go Back
          </button>
          {isAdmin && (
            <button
              onClick={() => navigate("/classes")}
              className="btn-primary text-sm"
            >
              Create Class
            </button>
          )}
        </div>
      </div>
    );
  }

  if (isTeacher && form.unitId && !teacherUnitIds.includes(form.unitId)) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-semibold text-gray-900">
            Access restricted
          </h2>
          <p className="text-gray-600">
            Teachers can only add/edit students in their assigned classes.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl py-4 relative">
      {/* Global Processing Loader */}
      {loading && <LoadingOverlay />}

      <h1 className="mb-6 text-xl font-bold text-gray-900 px-2">
        {id ? "Edit Student" : "Add Student"}
      </h1>
      <form onSubmit={handleSubmit} className="p-2 space-y-6">
        {/* Personal Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-bold text-gray-900 border-b pb-2 uppercase tracking-wider">
            Personal Information
          </h3>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            <div>
              <label className="block mb-1.5 text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full input-field"
                value={form.firstName}
                onChange={(e) =>
                  setForm({ ...form, firstName: e.target.value })
                }
                placeholder="Enter first name"
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
              )}
            </div>
            <div>
              <label className="block mb-1.5 text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                Middle Name
              </label>
              <input
                className="w-full input-field"
                value={form.middleName}
                onChange={(e) =>
                  setForm({ ...form, middleName: e.target.value })
                }
                placeholder="Enter middle name"
              />
            </div>
            <div>
              <label className="block mb-1.5 text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full input-field"
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                placeholder="Enter last name"
              />
              {errors.lastName && (
                <p className="mt-1 text-[11px] text-red-600">{errors.lastName}</p>
              )}
            </div>
            <div>
              <label className="block mb-1.5 text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                className="w-full input-field"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="student@email.com"
              />
              {errors.email && (
                <p className="mt-1 text-[11px] text-red-600">{errors.email}</p>
              )}
            </div>
            <div>
              <label className="block mb-1.5 text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                Registration ID <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full input-field"
                value={form.studentRegistrationId}
                onChange={(e) =>
                  setForm({ ...form, studentRegistrationId: e.target.value })
                }
                placeholder="Enter registration ID"
              />
              {errors.studentRegistrationId && (
                <p className="mt-1 text-[11px] text-red-600">
                  {errors.studentRegistrationId}
                </p>
              )}
            </div>
            
            {/* Class and Section Logic */}
            <div>
               <label className="block mb-1.5 text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                  Class <span className="text-red-500">*</span>
               </label>
               <select
                  className="w-full input-field"
                  value={form.unitId}
                  onChange={(e) => setForm({ ...form, unitId: e.target.value, sectionId: "" })}
               >
                  {allowedUnits.map((u) => (
                  <option key={u.id} value={u.id}>
                     {u.name}
                  </option>
                  ))}
               </select>
               {errors.unitId && (
                  <p className="mt-1 text-[11px] text-red-600">{errors.unitId}</p>
               )}
            </div>
            
            {/* Conditionally render Section Dropdown */}
            {(loadingSections || sections.length > 0) && (
               <div>
                  <label className="block mb-1.5 text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                     Section <span className="text-red-500">*</span>
                  </label>
                  {loadingSections ? (
                     <div className="flex items-center text-sm text-gray-500 h-[44px] px-3 bg-gray-50 border rounded-lg">
                        <Loader className="w-4 h-4 mr-2 animate-spin text-primary-500" />
                        Loading...
                     </div>
                  ) : (
                     <select
                        className="w-full input-field"
                        value={form.sectionId}
                        onChange={(e) => setForm({ ...form, sectionId: e.target.value })}
                     >
                        <option value="">Select Section</option>
                        {sections.map((sec) => (
                           <option key={sec._id} value={sec._id}>
                              {sec.sectionName || sec.name}
                           </option>
                        ))}
                     </select>
                  )}
                  {errors.sectionId && (
                     <p className="mt-1 text-[11px] text-red-600">{errors.sectionId}</p>
                  )}
               </div>
            )}

            <div>
              <label className="block mb-1.5 text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                Date of Birth <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                className="w-full input-field"
                value={form.dateOfBirth}
                onChange={(e) =>
                  setForm({ ...form, dateOfBirth: e.target.value })
                }
                onClick={(e) => e.target.showPicker && e.target.showPicker()}
              />
              {errors.dateOfBirth && (
                <p className="mt-1 text-[11px] text-red-600">
                  {errors.dateOfBirth}
                </p>
              )}
            </div>
            <div>
              <label className="block mb-1.5 text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                Gender <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full input-field"
                value={form.gender}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
              {errors.gender && (
                <p className="mt-1 text-[11px] text-red-600">{errors.gender}</p>
              )}
            </div>
            <div>
              <label className="block mb-1.5 text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                Phone Number
                {isPhoneRequired() ? (
                  <span className="text-red-500">*</span>
                ) : (
                  " (optional)"
                )}
              </label>
              <input
                className="w-full input-field"
                value={form.phoneNumber}
                onChange={(e) =>
                  setForm({ ...form, phoneNumber: e.target.value })
                }
                placeholder="+91 98765 43210"
              />
              {errors.phoneNumber && (
                <p className="mt-1 text-[11px] text-red-600">
                  {errors.phoneNumber}
                </p>
              )}
            </div>
            <div>
              <label className="block mb-1.5 text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                Alternate Phone
              </label>
              <input
                className="w-full input-field"
                value={form.alternatePhoneNumber}
                onChange={(e) =>
                  setForm({ ...form, alternatePhoneNumber: e.target.value })
                }
                placeholder="+91 98765 43211"
              />
            </div>
            <div>
              <label className="block mb-1.5 text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                Admission Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                className="w-full input-field"
                value={form.admissionDate}
                onChange={(e) =>
                  setForm({ ...form, admissionDate: e.target.value })
                }
                onClick={(e) => e.target.showPicker && e.target.showPicker()}
              />
              {errors.admissionDate && (
                <p className="mt-1 text-[11px] text-red-600">
                  {errors.admissionDate}
                </p>
              )}
            </div>
            <div>
              <label className="block mb-1.5 text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                Status
              </label>
              <select
                className="w-full input-field"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="graduated">Graduated</option>
                <option value="transferred">Transferred</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-sm font-bold text-gray-900 border-b pb-2 uppercase tracking-wider">
            Address Information
          </h3>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="block mb-1.5 text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                Street Address <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full input-field"
                value={form.address.street}
                onChange={(e) =>
                  setForm({
                    ...form,
                    address: { ...form.address, street: e.target.value },
                  })
                }
                placeholder="Enter street address"
              />
              {errors.street && (
                <p className="mt-1 text-[11px] text-red-600">{errors.street}</p>
              )}
            </div>
            <div>
              <label className="block mb-1.5 text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                City <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full input-field"
                value={form.address.city}
                onChange={(e) =>
                  setForm({
                    ...form,
                    address: { ...form.address, city: e.target.value },
                  })
                }
                placeholder="Enter city"
              />
              {errors.city && (
                <p className="mt-1 text-[11px] text-red-600">{errors.city}</p>
              )}
            </div>
            <div>
              <label className="block mb-1.5 text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                State <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full input-field"
                value={form.address.state}
                onChange={(e) =>
                  setForm({
                    ...form,
                    address: { ...form.address, state: e.target.value },
                  })
                }
                placeholder="Enter state"
              />
              {errors.state && (
                <p className="mt-1 text-[11px] text-red-600">{errors.state}</p>
              )}
            </div>
            <div>
              <label className="block mb-1.5 text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                Postal Code <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full input-field"
                value={form.address.postalCode}
                onChange={(e) =>
                  setForm({
                    ...form,
                    address: { ...form.address, postalCode: e.target.value },
                  })
                }
                placeholder="Enter postal code"
              />
              {errors.postalCode && (
                <p className="mt-1 text-[11px] text-red-600">{errors.postalCode}</p>
              )}
            </div>
            <div>
              <label className="block mb-1.5 text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                Country <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full input-field"
                value={form.address.country}
                onChange={(e) =>
                  setForm({
                    ...form,
                    address: { ...form.address, country: e.target.value },
                  })
                }
                placeholder="Enter country"
              />
              {errors.country && (
                <p className="mt-1 text-[11px] text-red-600">{errors.country}</p>
              )}
            </div>
          </div>
        </div>

        {/* Parent Information */}
        {/* Parent Information */}
        <div className="space-y-4">
          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">
              Parent Information (Optional)
            </h3>
            <button
              type="button"
              onClick={() => setShowParentForm(true)}
              className="px-4 py-2 text-xs font-bold rounded-lg border transition-colors"
              style={{ 
                color: 'var(--primary-color, #4f46e5)',
                backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)',
                borderColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 30%, white)'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--primary-color, #4f46e5) 15%, white)'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)'}
            >
              ➕ Create New Parent
            </button>
          </div>

          <div className="relative">
            {!selectedParent ? (
              <div className="space-y-2">
                <label className="block text-[12px] font-bold text-gray-600 uppercase tracking-tight">
                  Search & Link Parent
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    className="w-full input-field pl-10"
                    placeholder="Search by parent name or phone number..."
                    value={parentSearchTerm}
                    onChange={(e) => setParentSearchTerm(e.target.value)}
                  />
                </div>
                
                {showParentResults && parentResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-1">
                    {parentResults.map((parent) => (
                      <button
                        key={parent._id}
                        type="button"
                        onClick={() => {
                          setForm(prev => ({ ...prev, parentId: parent._id }));
                          setParentSearchTerm("");
                          setShowParentResults(false);
                        }}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-primary-50 border-b last:border-0 transition-colors group text-left"
                      >
                        <div>
                          <p className="text-sm font-bold text-gray-900 group-hover:text-primary-700">
                            {parent.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {parent.phoneNumber} • {parent.relation}
                          </p>
                        </div>
                        <CheckCircle2 className="w-5 h-5 text-gray-300 group-hover:text-primary-500 transition-colors" />
                      </button>
                    ))}
                  </div>
                )}
                {showParentResults && parentResults.length === 0 && parentSearchTerm.length > 1 && (
                   <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg p-4 shadow-xl text-center">
                      <p className="text-sm text-gray-600">No parents found with "{parentSearchTerm}"</p>
                      <button
                        type="button"
                        onClick={() => setShowParentForm(true)}
                        className="mt-2 text-xs font-bold text-primary-600 hover:underline"
                      >
                        Create a new parent instead
                      </button>
                   </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between p-4 bg-primary-50 border border-primary-200 rounded-xl animate-in fade-in zoom-in-95 duration-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 uppercase">
                      {selectedParent.name}
                    </h4>
                    <p className="text-xs text-primary-700 font-medium flex flex-wrap gap-x-2">
                      <span>{selectedParent.relation}</span>
                      <span>•</span>
                      <span>{selectedParent.phoneNumber}</span>
                      {selectedParent.email && (
                        <>
                          <span>•</span>
                          <span className="italic lowercase">{selectedParent.email}</span>
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, parentId: "" }))}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                  title="Remove Link"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
            {errors.parentId && (
              <p className="mt-1 text-[11px] text-red-600">{errors.parentId}</p>
            )}
          </div>

          <div className="p-3 border border-blue-100 rounded-lg bg-blue-50/50 flex items-start gap-3">
            <span className="text-lg">💡</span>
            <p className="text-[11px] text-blue-700 leading-relaxed pt-0.5">
               <strong>Search or Select:</strong> Quickly link an existing parent by searching for their name or phone number. 
               If the parent is not in the system, use the <strong>Create New Parent</strong> button above.
            </p>
          </div>
        </div>

        <div className="sticky bottom-0 flex items-center justify-end py-4 px-2 gap-3 bg-white/90 backdrop-blur-sm border-t mt-10 -mx-2">
          <button
            type="button"
            onClick={() => navigate("/students")}
            className="btn-secondary text-sm px-6 py-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary text-sm px-8 py-2 font-bold disabled:bg-gray-400 disabled:cursor-not-allowed shadow-md"
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <Loader className="w-4 h-4 animate-spin" />
                <span>{id ? "Updating..." : "Creating..."}</span>
              </div>
            ) : (
              id ? "Update Student" : "Create Student"
            )}
          </button>
        </div>
      </form>

      {/* Parent Form Modal */}
      {showParentForm && (
        <ParentForm
          onSubmit={handleFormSubmit}
          onClose={() => setShowParentForm(false)}
          simplified={true}
          isSubmitting={parentSubmitting}
        />
      )}
    </div>
  );
};

export default StudentForm;
